import { Property } from '../../../../types/Property/Property'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'
import multiversxService from '../../../../services/api-multiversx'
import { MultiversxNft } from '../../../../types/MultiversX/Nft'
import Card from '../../../Card'
import HelpCard from './HelpCard'
import Link from 'next/link'
import { HeroIcons } from '../../../../lib/utils/heroIcons'
import { Tooltip } from 'react-tooltip'
import { displayAddress } from '../../../../lib/utils/multiversx'
import { AxiosError } from 'axios'

type BlockchainSectionProps = {
  property: Property
}

const MX_EXPLORER_URL = process.env.NEXT_PUBLIC_MX_EXPLORER_URL

export default function BlockchainSection({
  property,
}: BlockchainSectionProps) {
  const { t } = useTranslation('originals/property')

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [multiversxNft, setMultiversxNft] = useState<MultiversxNft>()

  const fetchMultiversx = async () => {
    setIsLoading(true)
    try {
      const multiversxNft = await multiversxService.getCollectionSfts(
        property.nftCollectionId,
        property.nftNonce,
      )
      setMultiversxNft(multiversxNft[0])
    } catch (e: any | AxiosError) {
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchMultiversx()
  }, [])

  if (isLoading) {
    return (
      <>
        <HelpCard />
        <div className="flex items-center py-8">
          <div className="mx-auto flex items-center">
            <HeroIcons.ArrowPathIcon className="h-4 w-4 animate-spin" />
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="flex-col space-y-3">
      <HelpCard />

      {multiversxNft ? (
        <Card className="relative block h-full w-full border border-neutralGray/50 bg-neutralWhite md:flex">
          <div className="relative w-auto overflow-hidden rounded-bl-none rounded-tl-2xl rounded-tr-2xl bg-neutralBlack/20 md:w-1/4 md:rounded-bl-2xl md:rounded-tr-none">
            <img
              className="h-full w-full object-cover"
              src={multiversxNft.url}
              alt="Nft image"
            />
            <HeroIcons.NewspaperIcon className="absolute left-1/2 top-1/2 -z-10 w-12 -translate-x-1/2 -translate-y-1/2 transform text-neutralBlack/60" />
          </div>
          <div className="flex flex-grow flex-col">
            <div className="flex flex-grow flex-col border-b">
              <div className="m-3 flex flex-grow flex-col">
                <div className="">
                  <div className="text-3xl font-medium text-neutralBlack">
                    {multiversxNft.name}
                  </div>
                </div>
                <div className="mt-3 flex flex-grow items-center">
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-start">
                        <div className="text-md font-thin text-neutralBlack">
                          {t('blockchainSection.tokenIdentifier.title')}
                        </div>
                        <HeroIcons.InformationCircleIcon
                          className="-mt-0.5 ml-0.5 h-auto w-4 text-neutralBlack/90"
                          data-tooltip-id="tokenIdentifierTooltip"
                        />
                        <Tooltip
                          place="top"
                          id="tokenIdentifierTooltip"
                          className="max-w-[95%] sm:max-w-[80%]"
                        >
                          {t('blockchainSection.tokenIdentifier.tooltip')}
                        </Tooltip>
                      </div>
                      <Link
                        href={`${MX_EXPLORER_URL}/nfts/${multiversxNft.identifier}`}
                        target="_blank"
                        rel="noopener nofollow noreferrer external"
                        className="flex items-center space-x-1"
                      >
                        <div className="text-sm font-semibold text-neutralBlack">
                          {multiversxNft.identifier}
                        </div>
                        <HeroIcons.LinkIcon className="h-auto w-3 text-neutralBlack/80" />
                      </Link>
                    </div>
                    <div>
                      <div className="flex items-start">
                        <div className="text-md font-thin text-neutralBlack">
                          {t('blockchainSection.supply.title')}
                        </div>
                        <HeroIcons.InformationCircleIcon
                          className="-mt-0.5 ml-0.5 h-auto w-4 text-neutralBlack/90"
                          data-tooltip-id="supplyTooltip"
                        />
                        <Tooltip
                          place="top"
                          id="supplyTooltip"
                          className="max-w-[95%] sm:max-w-[80%]"
                        >
                          {t('blockchainSection.supply.tooltip')}
                        </Tooltip>
                      </div>
                      <div className="text-sm font-semibold text-neutralBlack">
                        {multiversxNft.supply}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="m-3">
                <div className="">
                  <div className="flex items-start">
                    <div className="text-xs font-thin text-neutralBlack">
                      {t('blockchainSection.smartContract.title')}
                    </div>
                    <HeroIcons.InformationCircleIcon
                      className="-mt-0.5 ml-0.5 h-auto w-4 text-neutralBlack/90"
                      data-tooltip-id="smartContractTooltip"
                    />
                    <Tooltip
                      place="top"
                      id="smartContractTooltip"
                      className="max-w-[95%] sm:max-w-[80%]"
                    >
                      {t('blockchainSection.smartContract.tooltip')}
                    </Tooltip>
                  </div>
                  <Link
                    href={`${MX_EXPLORER_URL}/accounts/${multiversxNft.creator}`}
                    target="_blank"
                    rel="noopener nofollow noreferrer external"
                    className="flex items-start space-x-0.5"
                  >
                    <div className="hidden text-xs font-semibold text-neutralBlack md:block">
                      {multiversxNft.creator}
                    </div>

                    <div className="block text-xs font-semibold text-neutralBlack md:hidden">
                      {displayAddress(multiversxNft.creator)}
                    </div>

                    <HeroIcons.LinkIcon className="-mt-0.5 h-auto w-3 text-neutralBlack/80" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="flex items-center">
          <div className="mx-auto py-8 font-bold text-neutralBlack">
            {t('blockchainSection.noTokenFound')}
          </div>
        </div>
      )}
    </div>
  )
}
